/* eslint-disable react-hooks/set-state-in-effect */
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useEffect, useMemo, useState } from 'react';
import { useMatches, useNavigate } from 'react-router-dom';
import { type AdminRouterItem, routes } from '../../router';

const useStyles = makeStyles({
  container: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  breadcrumb: {
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumbItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  link: {
    textDecoration: 'none',
    color: tokens.colorNeutralForeground1,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  button: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
  },
  divider: {
    color: tokens.colorNeutralForeground3,
    margin: `0 ${tokens.spacingHorizontalXS}`,
  }
});

interface RouteInfo {
  path: string;
  title: string;
}

interface BreadcrumbItemType {
  title: string;
  path: string;
}

const flattenRoutes = (routes: AdminRouterItem[], prefix = '/'): Record<string, RouteInfo> => {
  let map: Record<string, RouteInfo> = {};

  routes.forEach(itm => {
    if (!itm.meta?.title || !itm.path) return;

    map[prefix + itm.path] = {
      path: prefix + itm.path,
      title: itm.meta.title
    };

    if (itm.children) {
      map = Object.assign({}, map, flattenRoutes(itm.children, prefix + itm.path + '/'));
    }
  });

  return map;
};

const PageBreadcrumb: React.FC = () => {
  const styles = useStyles();
  const matches = useMatches();
  const navigate = useNavigate();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemType[]>([]);

  const flattendRoutes = useMemo(() => flattenRoutes(routes), []);

  useEffect(() => {
    setBreadcrumbs(
      matches
        .map((match): BreadcrumbItemType | null => {
          const routeInfo = flattendRoutes[match.pathname];
          if (!routeInfo) return null;
          return {
            title: routeInfo.title,
            path: match.pathname
          };
        })
        .filter((item): item is BreadcrumbItemType => item !== null)
    );
  }, [matches, flattendRoutes]);

  if (breadcrumbs.length === 0) return null;

  if (breadcrumbs.length === 1) {
    return (
      <div className={styles.container}>
        <Breadcrumb className={styles.breadcrumb}>
          <div key={breadcrumbs[0].path} className={styles.breadcrumbItem}>
            <BreadcrumbButton
              type='button'
              current
              className={styles.button}
            >
              {breadcrumbs[0].title}
            </BreadcrumbButton>
            <span style={{ display: 'none' }} role="listitem">
              <BreadcrumbDivider className={styles.divider}>/</BreadcrumbDivider>
              <span>placeholder</span>
            </span>
          </div>
        </Breadcrumb>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Breadcrumb className={styles.breadcrumb}>
        {breadcrumbs.map((item, index) => (
          <div key={item.path} className={styles.breadcrumbItem}>
            {index === breadcrumbs.length - 1 ? (
              <BreadcrumbButton
                type='button'
                current
                className={styles.button}
              >
                {item.title}
              </BreadcrumbButton>
            ) : (
              <>
                <BreadcrumbButton
                  type='button'
                  className={styles.link}
                  onClick={() => navigate(item.path)}
                >
                  {item.title}
                </BreadcrumbButton>
                <BreadcrumbDivider className={styles.divider}>/</BreadcrumbDivider>
              </>
            )}
          </div>
        ))}
      </Breadcrumb>
    </div>
  );
};

export default PageBreadcrumb;
